// init state
import { Account, CountryCode, UserMe, UserRole } from '@brenger/api-client';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { RootState } from '../../types/state';
import { PERSIST_VERSION } from '../../utils/global';
import { User, UserActionTypes } from './typings';

export const defaultState: User = {
  id: null,
  fetching: false,
  loggedIn: false,
  loggedInMethod: null,
  userData: null,
  errors: '',
  details: null,
  loading: false,
  registered: false,
  isBusiness: false,
  what_is_my_country: 'NL',
  billingAddress: null,
};

// actions
export const actions = {
  setWhatIsMyCountry: (country: CountryCode) => ({ type: UserActionTypes.SET_WHAT_IS_MY_COUNTRY, payload: country }),
  socialAuthStart: (service, registered) => ({ type: UserActionTypes.SOCIAL_AUTH_START, service, registered }),
  register: user => ({ type: UserActionTypes.REGISTER_USER_START, payload: user }),
  registerFailed: (message: string) => ({ type: UserActionTypes.REGISTER_USER_FAILED, payload: message }),
  registerSuccess: user => ({ type: UserActionTypes.REGISTER_USER_SUCCESS, payload: user }),
  registerErrorReset: () => ({ type: UserActionTypes.REGISTER_USER_ERROR_RESET }),
  socialAuthSuccess: user => ({ type: UserActionTypes.SOCIAL_AUTH_SUCCESS, payload: user }),
  socialAuthFailed: user => ({ type: UserActionTypes.SOCIAL_AUTH_FAILED, payload: user }),
  setUser: (userDetails: UserMe | null) => ({ type: UserActionTypes.SET_USER_DETAILS, payload: userDetails }),
  setAccountType: isBusiness => ({ type: UserActionTypes.SET_ACCOUNT_TYPE, payload: isBusiness }),
  retrieveCurrentCountry: () => ({ type: UserActionTypes.RETRIEVE_CURRENT_COUNTRY }),
};

// selectors
export const getLoggedInUser = (state: RootState): User => {
  return state.user;
};

export const getAccountHasBusinessFlow = (state: RootState): boolean => {
  return !!(state.user.userData?.account as Account | undefined)?.has_business_flow;
};

export const getAccountIsPayingByInvoice = (state: RootState): boolean => {
  return !!(state.user.userData?.account as Account | undefined)?.paying_by_invoice;
};

export const getAccountIsEligibleForDeferredVat = (state: RootState): boolean => {
  return !!(state.user.userData?.account as Account | undefined)?.eligible_for_deferred_vat;
};

/**
 * A guest who books as a company with a VAT number that Core deems eligible for deferred (reverse-charge) VAT.
 * Only applies while the answer still matches the entered VAT number + country.
 */
export const getIsGuestEligibleForDeferredVat = (state: RootState): boolean => {
  if (state.user.loggedIn) return false;
  const customer = state.generalTransport?.contact?.customer;
  // Optional: persisted state from before this field existed doesn't have it
  const deferredVat = state.generalTransport?.deferred_vat;
  if (!customer?.is_business || !deferredVat?.eligible) return false;
  return (
    deferredVat.vat_number === customer.company_details?.vat_number &&
    deferredVat.country_code === customer.company_details?.address?.country_code
  );
};

export const getIsExcludingVat = (state: RootState): boolean => {
  return (
    getAccountIsPayingByInvoice(state) ||
    getAccountHasBusinessFlow(state) ||
    getAccountIsEligibleForDeferredVat(state) ||
    getIsGuestEligibleForDeferredVat(state)
  );
};

export const getUserRoles = (state: RootState): UserRole[] => {
  return state.user.userData?.roles || [];
};

export const getWhatIsMyCountry = (state: RootState): CountryCode => {
  return state.user.what_is_my_country;
};

export const isUserLoggedIn = (state: RootState): boolean => {
  return state.user.loggedIn;
};

export const isRegistered = (state: RootState): boolean => {
  return state.user.registered;
};

export const isSocialRegistration = (state: RootState): boolean => {
  return getUserRoles(state).includes('ROLE_NEEDS_SOCIAL_REGISTRATION');
};

// reducers
export const userReducer = (state = defaultState, action): User => {
  switch (action.type) {
    case UserActionTypes.SET_ACCOUNT_TYPE:
      return {
        ...state,
        isBusiness: action.payload === 'business',
      };
    case UserActionTypes.SET_WHAT_IS_MY_COUNTRY:
      return {
        ...state,
        what_is_my_country: action.payload,
      };
    case UserActionTypes.SET_USER_DETAILS:
      return {
        ...state,
        userData: action.payload,
        loading: false,
        loggedIn: Boolean(action.payload),
        errors: '',
      };
    case UserActionTypes.REGISTER_USER_START:
      return {
        ...state,
        loading: true,
        errors: '',
      };
    case UserActionTypes.REGISTER_USER_SUCCESS:
      return {
        ...state,
        loading: false,
      };
    case UserActionTypes.REGISTER_USER_FAILED:
      return {
        ...state,
        loading: false,
        errors: action.payload,
      };
    case UserActionTypes.REGISTER_USER_ERROR_RESET:
      return {
        ...state,
        errors: '',
      };
    case UserActionTypes.SOCIAL_AUTH_START:
      return {
        ...state,
        fetching: true,
        errors: '',
        loggedInMethod: action.service,
        registered: action.registered,
      };
    case UserActionTypes.SOCIAL_AUTH_FAILED:
      return {
        ...state,
        fetching: false,
        errors: action.payload,
      };
    case UserActionTypes.SOCIAL_AUTH_SUCCESS:
      return {
        ...state,
        id: action.payload['@id'],
        fetching: false,
        errors: '',
        loggedIn: true,
        details: action.payload,
      };
    default:
      return state;
  }
};

const persistConfig = {
  key: 'user',
  version: PERSIST_VERSION,
  storage,
};

export default persistReducer(persistConfig, userReducer);
